// What an operator quotes to support when Power refuses a call: the error code and the trace id
// of the answer. Power answers a refusal as a Problem, Power IAM as `{ code, message }`, and a
// proxy in front of either with any text, so the admin reads the body leniently: the code is the
// first entry code, else the top-level one; a trace that is not a valid W3C trace id is dropped
// rather than shown.
import { Array as Arr, Option, Schema } from 'effect';
import { TraceId } from '@finnest/core/effect';
import type { ProblemDetail } from '@finnest/core/effect';

export const PowerRefusal = Schema.Struct({
  code: Schema.NonEmptyString,
  traceId: Schema.NullOr(TraceId),
}).annotate({
  title: 'PowerAdminRefusal',
  description: 'The error code and the trace id of a Power refusal, to quote to support.',
});
export type PowerRefusal = typeof PowerRefusal.Type;

// Each field is read on its own, so a malformed trace keeps the code and the code of an entry
// does not depend on the others.
const RefusalBody = Schema.fromJsonString(
  Schema.Struct({
    code: Schema.optionalKey(Schema.Unknown),
    traceId: Schema.optionalKey(Schema.Unknown),
    errors: Schema.optionalKey(Schema.Unknown),
  }),
);
const decodeBody = Schema.decodeUnknownOption(RefusalBody);
const decodeEntries = Schema.decodeUnknownOption(Schema.Array(Schema.Unknown));
const decodeCode = Schema.decodeUnknownOption(Schema.NonEmptyString);
const decodeEntry = Schema.decodeUnknownOption(Schema.Struct({ code: Schema.NonEmptyString }));
const decodeTraceId = Schema.decodeUnknownOption(TraceId);

/**
 * The code of Power's 503 when Power IAM names no single organization operating the participant
 * (none, or several self-hosted ones): every deployment-scoped admin surface answers it until the
 * operator organization is provisioned.
 */
export const OPERATOR_ORGANIZATION_UNRESOLVED_CODE = 'OPERATOR_ORGANIZATION_UNRESOLVED';

/** The refusal a Power or Power IAM answer names, or none when its body names no code. */
export const powerRefusalOf = (text: string): Option.Option<PowerRefusal> =>
  Option.flatMap(decodeBody(text), (body) =>
    Option.map(
      Option.orElse(
        Option.flatMap(decodeEntries(body.errors), (errors) =>
          Arr.findFirst(errors, (entry) => Option.map(decodeEntry(entry), ({ code }) => code)),
        ),
        () => decodeCode(body.code),
      ),
      (code) => ({ code, traceId: Option.getOrNull(decodeTraceId(body.traceId)) }),
    ),
  );

/** The refusal of a Problem already decoded. */
export const problemRefusal = (problem: ProblemDetail): PowerRefusal => ({
  code: Option.getOrElse(
    Option.flatMap(Option.fromUndefinedOr(problem.errors), (errors) =>
      Arr.findFirst(errors, ({ code }) => Option.fromUndefinedOr(code)),
    ),
    () => problem.code,
  ),
  traceId: Option.getOrNull(Option.fromUndefinedOr(problem.traceId)),
});
